import React, { useState } from 'react';
import styled from 'styled-components';
import { useDesktop } from '../../contexts/DesktopContext';

const SettingsContainer = styled.div`
  display: flex;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  color: #00ff00;
  font-family: 'Ubuntu', sans-serif;
`;

const Sidebar = styled.div`
  width: 200px;
  background: rgba(0, 0, 0, 0.8);
  border-right: 1px solid rgba(0, 255, 0, 0.3);
  padding: 20px 0;
`;

const SidebarItem = styled.div<{ active: boolean }>`
  padding: 12px 20px;
  cursor: pointer;
  background: ${props => props.active ? 'rgba(0, 255, 0, 0.2)' : 'transparent'};
  border-left: 3px solid ${props => props.active ? '#00ff00' : 'transparent'};
  color: ${props => props.active ? '#00ff00' : '#cccccc'};
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 255, 0, 0.1);
    color: #00ff00;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 30px;
  overflow-y: auto;
`;

const SettingSection = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  color: #00ffff;
  margin-bottom: 15px;
  border-bottom: 1px solid rgba(0, 255, 255, 0.3);
  padding-bottom: 5px;
`;

const SettingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 255, 0, 0.1);
`;

const SettingLabel = styled.div`
  color: #cccccc;
`;

const SettingControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Toggle = styled.button<{ active: boolean }>`
  width: 50px;
  height: 24px;
  border-radius: 12px;
  border: none;
  background: ${props => props.active ? '#00ff00' : '#333'};
  position: relative;
  cursor: pointer;
  transition: background 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${props => props.active ? '#000' : '#666'};
    top: 2px;
    left: ${props => props.active ? '28px' : '2px'};
    transition: all 0.3s ease;
  }
`;

const Select = styled.select`
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(0, 255, 0, 0.3);
  color: #00ff00;
  padding: 5px 10px;
  border-radius: 4px;
  font-family: inherit;

  option {
    background: #000;
    color: #00ff00;
  }
`;

const Slider = styled.input`
  width: 100px;
  height: 4px;
  border-radius: 2px;
  background: #333;
  outline: none;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #00ff00;
    cursor: pointer;
  }
`;

const Button = styled.button`
  background: rgba(0, 255, 0, 0.2);
  border: 1px solid #00ff00;
  color: #00ff00;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgba(0, 255, 0, 0.3);
  }
`;

const SystemInfo = styled.div`
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 8px;
  padding: 15px;
  margin-top: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const SystemSettings: React.FC = () => {
  const { wallpaper, setWallpaper, theme, setTheme } = useDesktop();
  const [activeSection, setActiveSection] = useState('display');
  const [settings, setSettings] = useState({
    animations: true,
    soundEffects: false,
    notifications: true,
    autoSave: true,
    darkMode: true,
    fontSize: 14,
    volume: 50,
    terminalTheme: 'matrix',
    language: 'en'
  });

  const sections = [
    { id: 'display', name: '🖥️ Display', icon: '🖥️' },
    { id: 'system', name: '⚙️ System', icon: '⚙️' },
    { id: 'privacy', name: '🔒 Privacy', icon: '🔒' },
    { id: 'about', name: 'ℹ️ About', icon: 'ℹ️' }
  ];

  const wallpapers = [
    { id: 'cybersecurity', name: 'Cybersecurity' },
    { id: 'matrix', name: 'Matrix' },
    { id: 'hacker', name: 'Hacker' },
    { id: 'terminal', name: 'Terminal' },
    { id: 'neon', name: 'Neon' },
    { id: 'dark', name: 'Dark' },
    { id: 'blue', name: 'Blue' },
    { id: 'red', name: 'Red' }
  ];

  const updateSetting = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const renderDisplaySettings = () => (
    <div>
      <SettingSection>
        <SectionTitle>Wallpaper & Theme</SectionTitle>
        <SettingItem>
          <SettingLabel>Wallpaper</SettingLabel>
          <SettingControl>
            <Select value={wallpaper} onChange={(e) => setWallpaper(e.target.value)}>
              {wallpapers.map(wp => (
                <option key={wp.id} value={wp.id}>{wp.name}</option>
              ))}
            </Select>
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>Dark Mode</SettingLabel>
          <SettingControl>
            <Toggle 
              active={settings.darkMode} 
              onClick={() => updateSetting('darkMode', !settings.darkMode)}
            />
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>Animations</SettingLabel>
          <SettingControl>
            <Toggle 
              active={settings.animations} 
              onClick={() => updateSetting('animations', !settings.animations)}
            />
          </SettingControl>
        </SettingItem>
      </SettingSection>

      <SettingSection>
        <SectionTitle>Text & Display</SectionTitle>
        <SettingItem>
          <SettingLabel>Font Size</SettingLabel>
          <SettingControl>
            <Slider 
              type="range" 
              min="12" 
              max="20" 
              value={settings.fontSize}
              onChange={(e) => updateSetting('fontSize', e.target.value)}
            />
            <span>{settings.fontSize}px</span>
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>Terminal Theme</SettingLabel>
          <SettingControl>
            <Select value={settings.terminalTheme} onChange={(e) => updateSetting('terminalTheme', e.target.value)}>
              <option value="matrix">Matrix</option>
              <option value="hacker">Hacker</option>
              <option value="amber">Amber</option>
              <option value="kali">Kali Linux</option>
            </Select>
          </SettingControl>
        </SettingItem>
      </SettingSection>
    </div>
  );

  const renderSystemSettings = () => (
    <div>
      <SettingSection>
        <SectionTitle>System Preferences</SectionTitle>
        <SettingItem>
          <SettingLabel>Sound Effects</SettingLabel>
          <SettingControl>
            <Toggle 
              active={settings.soundEffects} 
              onClick={() => updateSetting('soundEffects', !settings.soundEffects)}
            />
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>Notifications</SettingLabel>
          <SettingControl>
            <Toggle 
              active={settings.notifications} 
              onClick={() => updateSetting('notifications', !settings.notifications)}
            />
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>Auto Save</SettingLabel>
          <SettingControl>
            <Toggle 
              active={settings.autoSave} 
              onClick={() => updateSetting('autoSave', !settings.autoSave)}
            />
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>Volume</SettingLabel>
          <SettingControl>
            <Slider 
              type="range" 
              min="0" 
              max="100" 
              value={settings.volume}
              onChange={(e) => updateSetting('volume', e.target.value)}
            />
            <span>{settings.volume}%</span>
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>Language</SettingLabel>
          <SettingControl>
            <Select value={settings.language} onChange={(e) => updateSetting('language', e.target.value)}>
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
            </Select>
          </SettingControl>
        </SettingItem>
      </SettingSection>

      <SettingSection>
        <SectionTitle>Storage & Performance</SectionTitle>
        <SettingItem>
          <SettingLabel>Clear Cache</SettingLabel>
          <SettingControl>
            <Button onClick={() => localStorage.clear()}>Clear</Button>
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>Reset Settings</SettingLabel>
          <SettingControl>
            <Button onClick={() => window.location.reload()}>Reset</Button>
          </SettingControl>
        </SettingItem>
      </SettingSection>
    </div>
  );

  const renderPrivacySettings = () => (
    <div>
      <SettingSection>
        <SectionTitle>Privacy & Security</SectionTitle>
        <SettingItem>
          <SettingLabel>Portfolio Analytics</SettingLabel>
          <SettingControl>
            <Toggle active={false} onClick={() => {}} />
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>Contact Form Data</SettingLabel>
          <SettingControl>
            <Button>Manage Data</Button>
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>Session Storage</SettingLabel>
          <SettingControl>
            <Button onClick={() => sessionStorage.clear()}>Clear Session</Button>
          </SettingControl>
        </SettingItem>
      </SettingSection>
    </div>
  );

  const renderAboutSettings = () => (
    <div>
      <SettingSection>
        <SectionTitle>System Information</SectionTitle>
        <SystemInfo>
          <InfoRow>
            <span>Portfolio Version:</span>
            <span>2.0.1</span>
          </InfoRow>
          <InfoRow>
            <span>React Version:</span>
            <span>18.2.0</span>
          </InfoRow>
          <InfoRow>
            <span>Build Date:</span>
            <span>{new Date().toLocaleDateString()}</span>
          </InfoRow>
          <InfoRow>
            <span>Developer:</span>
            <span>Mukul Joshi</span>
          </InfoRow>
          <InfoRow>
            <span>License:</span>
            <span>MIT</span>
          </InfoRow>
        </SystemInfo>
      </SettingSection>

      <SettingSection>
        <SectionTitle>Contact Developer</SectionTitle>
        <SettingItem>
          <SettingLabel>Email</SettingLabel>
          <SettingControl>
            <Button onClick={() => window.open('mailto:memukuljoshi@gmail.com')}>
              Send Email
            </Button>
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>GitHub</SettingLabel>
          <SettingControl>
            <Button onClick={() => window.open('https://github.com/HeyMeMukul')}>
              View Profile
            </Button>
          </SettingControl>
        </SettingItem>
        <SettingItem>
          <SettingLabel>LinkedIn</SettingLabel>
          <SettingControl>
            <Button onClick={() => window.open('https://www.linkedin.com/in/memukuljoshi/')}>
              Connect
            </Button>
          </SettingControl>
        </SettingItem>
      </SettingSection>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'display': return renderDisplaySettings();
      case 'system': return renderSystemSettings();
      case 'privacy': return renderPrivacySettings();
      case 'about': return renderAboutSettings();
      default: return renderDisplaySettings();
    }
  };

  return (
    <SettingsContainer>
      <Sidebar>
        {sections.map(section => (
          <SidebarItem
            key={section.id}
            active={activeSection === section.id}
            onClick={() => setActiveSection(section.id)}
          >
            {section.name}
          </SidebarItem>
        ))}
      </Sidebar>
      <ContentArea>
        <h2 style={{ color: '#00ffff', marginBottom: '20px' }}>
          {sections.find(s => s.id === activeSection)?.name || 'Settings'}
        </h2>
        {renderContent()}
      </ContentArea>
    </SettingsContainer>
  );
};

export default SystemSettings;
